import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarDays, ClipboardList, ListChecks, Plus, UserSquare2,
} from "lucide-react";
import PilotBanner from "@/modules/racket_lab_admin/PilotBanner";
import RLNavSidebar from "@/modules/racket_lab_admin/RLNavSidebar";
import FlagBadge from "@/modules/racket_lab_admin/FlagBadge";
import TicketCreateDialog from "@/modules/racket_lab_admin/TicketCreateDialog";
import useAdminApi from "@/hooks/useAdminApi";
import useRLContext from "@/hooks/useRLContext";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";
import { TICKET_STATUS_LABEL_KEY, labelKeyFor } from "@/i18n/racketLabMaps";

/**
 * RLAdminDashboard — Sub-sprint A read-only view + SS-2.2 (Jun 2026)
 * operational landing.
 *
 * SS-2.2 adds (above the existing ops cards, which are untouched):
 *   • Quick actions: New ticket (opens TicketCreateDialog inline),
 *     Tickets, My tickets, Calendar (disabled placeholder until SS-4).
 *   • Stat tiles + work queue + recent tickets fed by the new admin-only
 *     read-only endpoint `GET /admin/racket-lab/dashboard-summary`.
 *   • "Most used tensions by shop" widget built on the SS-2.1 structured
 *     stringing fields.
 *   • Agenda block reserving the SS-4 calendar spot (open shifts count).
 *
 * Existing ops cards (promotion-overview, dispatch-status, redis-health,
 * legacy-memberships) keep their endpoints, copy and `data-testid`s.
 *
 * HARD BLOCKS preserved: admin-only, preview-first, no customer-facing,
 * no legal runtime, no Q28/emails, no public scan, no production.
 */
const StatCard = ({ title, loading, error, children, dataTestId, t }) => (
  <div
    data-testid={dataTestId}
    className="rounded-md border border-slate-200 bg-white p-4 shadow-sm"
  >
    <h3 className="mb-2 text-sm font-semibold text-slate-700">{title}</h3>
    {loading && (
      <p className="text-xs text-slate-500">{t("dashboard.loading")}</p>
    )}
    {!loading && error && (
      <p className="text-xs text-rose-700">
        {t("dashboard.error_prefix")}: {error.status || ""} {error.detail || t("dashboard.error_unknown")}
      </p>
    )}
    {!loading && !error && children}
  </div>
);

const StatTile = ({ label, value, testId, onClick }) =>
  onClick ? (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="rounded-md border border-slate-200 bg-white px-3 py-2 text-left shadow-sm hover:border-emerald-300 hover:bg-emerald-50"
    >
      <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-lg font-semibold text-slate-900">{value ?? "—"}</p>
    </button>
  ) : (
    <div data-testid={testId} className="rounded-md border border-slate-200 bg-white px-3 py-2 shadow-sm">
      <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-lg font-semibold text-slate-900">{value ?? "—"}</p>
    </div>
  );

const QuickAction = ({ icon: Icon, label, onClick, testId, disabled, hint }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    disabled={disabled}
    title={hint}
    className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium ${
      disabled
        ? "cursor-not-allowed border border-slate-200 bg-slate-50 text-slate-400"
        : "bg-emerald-600 text-white hover:bg-emerald-700"
    }`}
  >
    <Icon className="h-3.5 w-3.5" />
    {label}
    {hint ? <span className="text-[9px] font-normal opacity-80">· {hint}</span> : null}
  </button>
);

const ACTIVE_STATUSES = [
  "draft", "checked_in", "assigned", "in_progress",
  "pending_customer_approval", "ready_for_pickup",
];

const RLAdminDashboard = () => {
  const t = useRacketLabAdminT();
  const navigate = useNavigate();

  // SS-8/CP-1.3 — scoped dashboard helpers. Ops hooks are skipped for
  // non-(admin/staff) profiles so no forbidden calls / error toasts fire.
  const { ctx: rlCtx } = useRLContext();
  const profile = rlCtx?.profile;
  const isOpsViewer = profile === "admin" || profile === "staff";
  const canCreate = ["admin", "staff", "shop_manager", "qa_tester"].includes(profile);
  const has = (sec) => (rlCtx?.sections || []).includes(sec);

  const summary = useAdminApi("/admin/racket-lab/dashboard-summary");
  const overview = useAdminApi("/admin/racket-lab/_ops/promotion-overview", { skip: !isOpsViewer });
  const dispatchStatus = useAdminApi(
    "/admin/notifications/_ops/dispatch-status",
    { skip: !isOpsViewer },
  );
  const redisHealth = useAdminApi("/admin/qr-tokens/_ops/redis-health", { skip: !isOpsViewer });
  const legacy = useAdminApi(
    "/admin/racket-lab/_ops/legacy-memberships-overview",
    { skip: !isOpsViewer },
  );
  const [dialogOpen, setDialogOpen] = useState(false);

  const s = summary.data;
  const statusCounts = s?.tickets?.status_counts || {};

  const onCreated = (ticket) => {
    setDialogOpen(false);
    summary.reload?.();
    if (ticket?.id) navigate(`/admin/racket-lab/tickets/${ticket.id}`);
  };

  return (
    <div
      data-testid="rl-admin-dashboard-page"
      className="flex flex-col gap-4"
    >
      <PilotBanner />
      <RLNavSidebar />

      <header className="flex items-baseline justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">
            {t("dashboard.page_title")}
          </h1>
          <p className="text-xs text-slate-500">
            {t("dashboard.page_subtitle", { org: "TEAMSOFTWAREINNOVATIONS" })}
          </p>
        </div>
      </header>

      {/* SS-8/CP-1.3 — scoped-view banner for non-global profiles. */}
      {s?.viewer?.profile && !["admin", "staff"].includes(s.viewer.profile) && (
        <div
          data-testid="rl-dash-scoped-banner"
          className="rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-[11px] text-amber-800"
        >
          {t("dashboard.landing.scoped_banner", { profile: s.viewer.profile })}
        </div>
      )}

      {/* ============ SS-2.2 · Quick actions ============ */}
      <div data-testid="rl-dash-quick-actions" className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] uppercase tracking-wide text-slate-500">
          {t("dashboard.landing.quick_actions_title")}
        </span>
        {canCreate && (
          <QuickAction icon={Plus} label={t("dashboard.landing.quick_new_ticket")} onClick={() => setDialogOpen(true)} testId="rl-dash-quick-new-ticket" />
        )}
        {has("tickets") && (
          <QuickAction icon={ClipboardList} label={t("dashboard.landing.quick_tickets")} onClick={() => navigate("/admin/racket-lab/tickets")} testId="rl-dash-quick-tickets" />
        )}
        {has("my_tickets") && (
          <QuickAction icon={UserSquare2} label={t("dashboard.landing.quick_my_tickets")} onClick={() => navigate("/admin/racket-lab/my-tickets")} testId="rl-dash-quick-my-tickets" />
        )}
        {has("calendar") && (
          <QuickAction icon={CalendarDays} label={t("dashboard.landing.quick_calendar")} onClick={() => navigate("/admin/racket-lab/calendar")} testId="rl-dash-quick-calendar" />
        )}
      </div>

      {/* ============ SS-2.2 · Stat tiles ============ */}
      <div data-testid="rl-dash-stats" className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-8">
        <StatTile label={t("dashboard.landing.stat_tickets_total")} value={s?.tickets?.total} testId="rl-dash-stat-tickets-total" />
        <StatTile label={t("dashboard.landing.stat_active_queue")} value={s?.tickets?.active} testId="rl-dash-stat-active-queue" />
        <StatTile label={t("dashboard.landing.stat_in_progress")} value={statusCounts.in_progress ?? 0} testId="rl-dash-stat-in-progress" />
        <StatTile label={t("dashboard.landing.stat_ready_for_pickup")} value={statusCounts.ready_for_pickup ?? 0} testId="rl-dash-stat-ready-for-pickup" />
        <StatTile label={t("dashboard.landing.stat_operators_active")} value={s?.operators?.active} testId="rl-dash-stat-operators-active" />
        <StatTile label={t("dashboard.landing.stat_shops_active")} value={s?.shops?.active} testId="rl-dash-stat-shops-active" />
        <StatTile label={t("dashboard.landing.stat_open_shifts")} value={s?.shifts?.open} testId="rl-dash-stat-open-shifts" />
        {/* SS-8 — pending MOCK envelopes (draft, CP-1.5 scoped). */}
        <StatTile label={t("dashboard.landing.stat_pending_envelopes")} value={s?.envelopes?.pending_review} testId="rl-dash-stat-pending-envelopes" />
        {/* SS-6.1 — low-stock alerts (UI-only, scoped, zero notifications).
            micro (Jun 2026): clickable quick-access to Inventory pre-filtered
            to low-stock items, only for profiles with the inventory section. */}
        <StatTile
          label={t("dashboard.landing.stat_low_stock")}
          value={s?.inventory?.low_stock_count}
          testId="rl-dash-stat-low-stock"
          onClick={has("inventory") ? () => navigate("/admin/racket-lab/inventory?low_stock=1") : undefined}
        />
      </div>

      {/* CP-1.4 — QA/test universe note (separated from operational counters). */}
      {s?.qa?.tickets_total > 0 && (
        <p data-testid="rl-dash-qa-note" className="text-[10px] text-purple-700">
          {t("dashboard.landing.qa_note", { n: String(s.qa.tickets_total) })}
        </p>
      )}

      {/* ============ SS-2.2 · Queue + Recent + Tensions + Agenda ============ */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          title={t("dashboard.landing.queue_title")}
          loading={summary.loading}
          error={summary.error}
          dataTestId="rl-dash-queue"
          t={t}
        >
          {s?.tickets?.active ? (
            <ul className="space-y-1 text-xs">
              {ACTIVE_STATUSES.filter((st) => statusCounts[st]).map((st) => (
                <li key={st} className="flex items-center justify-between">
                  <span className="text-slate-500">{t(labelKeyFor(TICKET_STATUS_LABEL_KEY, st))}</span>
                  <span data-testid={`rl-dash-queue-${st}`} className="font-semibold">{statusCounts[st]}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-slate-500">{t("dashboard.landing.queue_empty")}</p>
          )}
        </StatCard>

        <StatCard
          title={t("dashboard.landing.recent_title")}
          loading={summary.loading}
          error={summary.error}
          dataTestId="rl-dash-recent"
          t={t}
        >
          {(s?.recent_tickets || []).length ? (
            <ul className="space-y-1.5 text-xs">
              {s.recent_tickets.map((tk) => (
                <li key={tk.id}>
                  <button
                    type="button"
                    data-testid={`rl-dash-recent-${tk.id}`}
                    onClick={() => navigate(`/admin/racket-lab/tickets/${tk.id}`)}
                    className="flex w-full items-center justify-between gap-2 rounded px-1 py-0.5 text-left hover:bg-slate-50"
                  >
                    <span className="truncate text-slate-700">{tk.equipment_reference_label || tk.id.slice(0, 8)}</span>
                    <span className="shrink-0 rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] text-slate-600">
                      {t(labelKeyFor(TICKET_STATUS_LABEL_KEY, tk.status))}
                    </span>
                  </button>
                </li>
              ))}
              <li>
                <button
                  type="button"
                  data-testid="rl-dash-recent-view-all"
                  onClick={() => navigate("/admin/racket-lab/tickets")}
                  className="mt-1 text-[10px] font-medium text-emerald-700 hover:underline"
                >
                  {t("dashboard.landing.recent_view_all")} →
                </button>
              </li>
            </ul>
          ) : (
            <p className="text-xs text-slate-500">{t("dashboard.landing.recent_empty")}</p>
          )}
        </StatCard>

        <StatCard
          title={t("dashboard.landing.tensions_title")}
          loading={summary.loading}
          error={summary.error}
          dataTestId="rl-dash-tensions"
          t={t}
        >
          {(s?.top_tensions_by_shop || []).length ? (
            <ul className="space-y-1 text-xs">
              {s.top_tensions_by_shop.map((row, i) => (
                <li key={i} data-testid={`rl-dash-tensions-row-${i}`} className="flex items-center justify-between gap-2">
                  <code className="truncate text-[10px] text-slate-500">{row.racket_lab_shop_id?.slice(0, 14)}</code>
                  <span className="font-semibold">{row.tension_main} {row.tension_unit}</span>
                  <span className="rounded-full bg-emerald-50 px-1.5 py-0.5 text-[9px] text-emerald-700">×{row.count}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-slate-500">{t("dashboard.landing.tensions_empty")}</p>
          )}
          <p className="mt-2 text-[9px] text-slate-400">{t("dashboard.landing.tensions_hint")}</p>
        </StatCard>

        <StatCard
          title={t("dashboard.landing.agenda_title")}
          loading={summary.loading}
          error={summary.error}
          dataTestId="rl-dash-agenda"
          t={t}
        >
          <p className="text-xs font-semibold text-slate-800">
            {t("dashboard.landing.agenda_open_shifts", { count: s?.shifts?.open ?? "—" })}
          </p>
          <p className="mt-2 text-[10px] uppercase tracking-wide text-slate-500">
            {t("dashboard.landing.agenda_today_title")}
          </p>
          {(s?.today_scheduled || []).length ? (
            <ul data-testid="rl-dash-agenda-today" className="mt-1 space-y-1 text-xs">
              {s.today_scheduled.map((tk) => (
                <li key={tk.id}>
                  <button
                    type="button"
                    data-testid={`rl-dash-agenda-today-${tk.id}`}
                    onClick={() => navigate(`/admin/racket-lab/tickets/${tk.id}`)}
                    className="flex w-full items-center justify-between gap-2 rounded px-1 py-0.5 text-left hover:bg-slate-50"
                  >
                    <span className="truncate text-slate-700">{tk.equipment_reference_label || tk.id.slice(0, 8)}</span>
                    <span className="shrink-0 rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] text-slate-600">
                      {t(labelKeyFor(TICKET_STATUS_LABEL_KEY, tk.status))}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p data-testid="rl-dash-agenda-today-empty" className="mt-1 text-[10px] text-slate-400">
              {t("dashboard.landing.agenda_today_empty")}
            </p>
          )}
          <button
            type="button"
            data-testid="rl-dash-agenda-open-calendar"
            onClick={() => navigate("/admin/racket-lab/calendar")}
            className="mt-2 inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2 py-1 text-[10px] font-medium text-white hover:bg-emerald-700"
          >
            <CalendarDays className="h-3 w-3" />
            {t("dashboard.landing.agenda_open_calendar")}
          </button>
        </StatCard>
      </div>

      {/* ============ RACKET-LAB-ROLE-1 · Catalog + CP cohort widgets ============ */}
      <div data-testid="rl-dash-role1-widgets" className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <StatCard
          title={t("dashboard.landing.catalog_title")}
          loading={summary.loading}
          error={summary.error}
          dataTestId="rl-dash-catalog-consumed"
          t={t}
        >
          <ul className="space-y-1 text-xs">
            <li className="flex items-center justify-between">
              <span className="text-slate-500">{t("dashboard.landing.catalog_products_available")}</span>
              <span data-testid="rl-dash-catalog-products" className="font-semibold">
                {s?.catalog_consumed?.products_available ?? "—"}
              </span>
            </li>
            <li className="flex items-center justify-between">
              <span className="text-slate-500">{t("dashboard.landing.catalog_brands_available")}</span>
              <span data-testid="rl-dash-catalog-brands" className="font-semibold">
                {s?.catalog_consumed?.brands_available ?? "—"}
              </span>
            </li>
            <li className="flex items-center justify-between">
              <span className="text-slate-500">{t("dashboard.landing.catalog_tickets_linked")}</span>
              <span data-testid="rl-dash-catalog-tickets" className="font-semibold">
                {s?.catalog_consumed?.tickets_with_catalog ?? "—"}
              </span>
            </li>
            <li className="flex items-center justify-between">
              <span className="text-slate-500">{t("dashboard.landing.catalog_inventory_linked")}</span>
              <span data-testid="rl-dash-catalog-inventory" className="font-semibold">
                {s?.catalog_consumed?.inventory_with_catalog ?? "—"}
              </span>
            </li>
          </ul>
          <p className="mt-2 text-[9px] text-slate-400">{t("dashboard.landing.catalog_hint")}</p>
        </StatCard>

        {s?.cp_cohort && (
          <StatCard
            title={t("dashboard.landing.cp_cohort_title")}
            loading={summary.loading}
            error={summary.error}
            dataTestId="rl-dash-cp-cohort"
            t={t}
          >
            <p className="text-xs font-semibold text-slate-800">
              {t("dashboard.landing.cp_cohort_count", { n: String(s.cp_cohort.count ?? 0) })}
            </p>
            <button
              type="button"
              data-testid="rl-dash-cp-cohort-open"
              onClick={() => navigate(s.cp_cohort.console_path || "/admin/controlled-production")}
              className="mt-2 inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2 py-1 text-[10px] font-medium text-white hover:bg-emerald-700"
            >
              {t("dashboard.landing.cp_cohort_open")} →
            </button>
            <p className="mt-2 text-[9px] text-slate-400">{t("dashboard.landing.cp_cohort_hint")}</p>
          </StatCard>
        )}
      </div>

      {/* ============ Ops / platform cards (pre-SS-2.2; SS-8: admin/staff only) ============ */}
      {isOpsViewer && (
        <>
          <h2 className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
            {t("dashboard.landing.ops_section_title")}
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          title={t("dashboard.promotion_overview")}
          loading={overview.loading}
          error={overview.error}
          dataTestId="rl-card-promotion-overview"
          t={t}
        >
          <ul className="space-y-1 text-xs">
            <li>
              <span className="text-slate-500">{t("dashboard.totals_total")}:</span>{" "}
              <span data-testid="rl-card-promotion-overview-total" className="font-semibold">
                {overview.data?.totals?.memberships_total ?? "—"}
              </span>
            </li>
            <li>
              <span className="text-slate-500">{t("dashboard.totals_active")}:</span>{" "}
              <span className="font-semibold">
                {overview.data?.totals?.active ?? "—"}
              </span>
            </li>
            <li>
              <span className="text-slate-500">{t("dashboard.totals_roles")}:</span>{" "}
              <span className="font-semibold">
                {Object.keys(overview.data?.counts_by_role || {}).length}
              </span>
            </li>
          </ul>
        </StatCard>

        <StatCard
          title={t("dashboard.dispatch_card_title")}
          loading={dispatchStatus.loading}
          error={dispatchStatus.error}
          dataTestId="rl-card-dispatch-status"
          t={t}
        >
          <div className="flex flex-wrap gap-1.5">
            <FlagBadge
              tone="info"
              labelKey="dispatch.flag_provider"
              value={dispatchStatus.data?.provider ?? "—"}
              dataTestId="rl-flag-provider"
            />
            <FlagBadge
              tone={dispatchStatus.data?.mock_enabled ? "mock" : "info"}
              labelKey="dispatch.flag_mock"
              value={dispatchStatus.data?.mock_enabled}
              dataTestId="rl-flag-mock-enabled"
            />
            <FlagBadge
              tone={dispatchStatus.data?.dispatch_enabled ? "hard" : "safe"}
              labelKey="dispatch.flag_dispatch"
              value={dispatchStatus.data?.dispatch_enabled}
              dataTestId="rl-flag-dispatch-enabled"
            />
            <FlagBadge
              tone={dispatchStatus.data?.q28_enabled ? "hard" : "safe"}
              labelKey="dispatch.flag_q28"
              value={dispatchStatus.data?.q28_enabled}
              dataTestId="rl-flag-q28-enabled"
            />
          </div>
          <p className="mt-2 text-[10px] text-slate-500">
            {t("dashboard.dispatch_outbox_line", {
              count: dispatchStatus.data?.outbox_count ?? "—",
              days: dispatchStatus.data?.ttl_days ?? "—",
            })}
          </p>
        </StatCard>

        <StatCard
          title={t("dashboard.redis_card_title")}
          loading={redisHealth.loading}
          error={redisHealth.error}
          dataTestId="rl-card-redis-health"
          t={t}
        >
          <FlagBadge
            tone={redisHealth.data?.healthy ? "safe" : "hard"}
            labelKey="dashboard.redis_flag_label"
            value={redisHealth.data?.healthy ? t("dashboard.redis_healthy") : t("dashboard.redis_down")}
            dataTestId="rl-flag-redis-health"
          />
          <p className="mt-2 text-[10px] text-slate-500">
            {t("dashboard.redis_backend_line", { backend: redisHealth.data?.backend ?? "—" })}
          </p>
        </StatCard>

        <StatCard
          title={t("dashboard.legacy_card_title")}
          loading={legacy.loading}
          error={legacy.error}
          dataTestId="rl-card-legacy-overview"
          t={t}
        >
          <ul className="space-y-1 text-xs">
            <li>
              <span className="text-slate-500">{t("dashboard.legacy_unknown_label")}:</span>{" "}
              <span className="font-semibold">
                {legacy.data?.unknown_total ??
                  legacy.data?.totals?.unknown ??
                  "—"}
              </span>
            </li>
            <li>
              <span className="text-slate-500">{t("dashboard.legacy_classified_label")}:</span>{" "}
              <span className="font-semibold">
                {legacy.data?.classified_total ??
                  legacy.data?.totals?.classified ??
                  "—"}
              </span>
            </li>
          </ul>
          </StatCard>
          </div>
        </>
      )}

      <TicketCreateDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={onCreated}
      />
    </div>
  );
};

export default RLAdminDashboard;
